import { Direction, BEAST } from './constants';
import type { PubSub } from '../PubSub';
import type { GameEvents } from '../events';

export type BeastState = string;

/** A large herbivore that beings can hunt cooperatively. */
export class Beast {
    public state: BeastState;
    public health: number;
    public x: number;
    public z: number;
    public moveDirection: Direction | null = null;
    public directionTicks: number = 0;
    public sleepTicks: number = 0;
    /** Spawn immunity, mirroring `Being.hasImmunity`. True on construction; while set
     *  the Beast takes no health decay, so one scattered into water or onto a mountain
     *  at level start survives long enough for `BeastFleeingState` to walk it out.
     *  `BeastController` clears it on the first tick the Beast is not in danger. */
    public hasImmunity: boolean = true;
    private readonly pubsub: PubSub<GameEvents>;
    /** Ticks of hunt credit remaining. Refreshed to `BEAST.KILL_CREDIT_TICKS` by every
     *  blow from a hunting being and counted down once per tick by `BeastController`.
     *  While above zero the Beast counts as "currently being hunted". */
    private huntCreditTicks: number = 0;
    /** Guards against announcing the same death twice. */
    private killAnnounced: boolean = false;

    /**
     * @param x - World-space X position.
     * @param z - World-space Z position.
     * @param pubsub - Event bus used by `damage()` to announce a hunt kill.
     * @param state - Initial state (defaults to `Sleeping`).
     * @param health - Initial health in [0, 1] (defaults to 1.0).
     */
    constructor(x: number, z: number, pubsub: PubSub<GameEvents>, state: BeastState = 'Sleeping', health = 1.0) {
        this.x = x;
        this.z = z;
        this.pubsub = pubsub;
        this.state = state;
        this.health = health;
    }

    /**
     * Sets health, clamped to [0, 1]. Death is announced from here rather than only
     * from `damage()`, so a Beast worn down by a hunt and finished off by terrain or
     * starvation still credits the pack.
     */
    public setHealth(value: number): void {
        this.health = Math.min(1.0, Math.max(0, value));
        this.announceIfKilled();
    }

    /**
     * Applies `amount` of damage from a hunting being and refreshes the hunt-credit
     * window. Any number of beings can damage the same Beast in a tick;
     * `announceIfKilled` fires at most once, so the kill is still attributed exactly once.
     */
    public damage(amount: number): void {
        if (this.health <= 0) return;
        this.huntCreditTicks = BEAST.KILL_CREDIT_TICKS;
        this.setHealth(this.health - amount);
    }

    /**
     * Ages the hunt-credit window by one tick. Called by `BeastController` at the end
     * of each tick, after health decay has had its chance to announce a death, so a
     * blow landed this tick always still counts.
     */
    public ageHuntCredit(): void {
        if (this.huntCreditTicks > 0) this.huntCreditTicks--;
    }

    /**
     * Publishes `world:beast-killed` the first time the Beast dies while a hunt is
     * still live — that is, within `BEAST.KILL_CREDIT_TICKS` of the last blow from a
     * being.
     *
     * Attribution deliberately does not require the *final* blow to come from a
     * being. A pack that wounds a Beast and drives it into the sea or off its
     * grazing land has hunted it just as surely as one that lands the last stone —
     * and using terrain to finish prey is the mechanic this level exists to teach.
     * The recency window is what stops an unhunted Beast that simply wandered into
     * water from being credited to the player. It is a window rather than a damage
     * threshold because the Beast heals far faster than hunters can wound it
     * (`BEAST.FOOD_RATE` outpaces `HuntingState.DAMAGE_PER_TICK`), so any cumulative
     * damage tally is erased by grazing long before it can be met.
     */
    private announceIfKilled(): void {
        if (this.killAnnounced) return;
        if (this.health > 0) return;
        if (this.huntCreditTicks <= 0) return;

        this.killAnnounced = true;
        this.pubsub.publish('world:beast-killed', { x: this.x, z: this.z });
    }

    /**
     * True while the Beast is still smarting from a recent attack — the same window
     * that credits a kill to the pack. An angered Beast gores beings that come within
     * `BEAST.GORE_RADIUS_SQ`, so wounding one and then crowding it is lethal.
     */
    public get isAngered(): boolean {
        return this.huntCreditTicks > 0;
    }

    /** Translates position by the given offsets. */
    public translate(dx: number, dz: number): void {
        this.x += dx;
        this.z += dz;
    }
}
